import { BRAND, BRAND_LONG } from "@/lib/brand";
import { applyBranding } from "@/lib/branding";
import { useEffect, useRef, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Upload, Save, Palette } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function fileToDataUrl(f) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(f);
  });
}

export default function BrandingSettings() {
  const { user } = useAuth();
  const isAdmin = ["admin", "hospital_admin", "platform_admin"].includes(user?.role);
  const fileRef = useRef(null);
  const [form, setForm] = useState({ logo_url: "", primary_color: "#1d4ed8", accent_color: "#06b6d4" });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/hospital-branding");
      const next = {
        logo_url: data.logo_url || "",
        primary_color: data.primary_color || "#1d4ed8",
        accent_color: data.accent_color || "#06b6d4",
      };
      setForm(next);
      applyBranding(next);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Max 2 MB logo"); return; }
    try {
      const url = await fileToDataUrl(f);
      setForm((s) => ({ ...s, logo_url: url }));
    } catch { toast.error("Couldn't read file"); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/hospital-branding", form);
      applyBranding(data);
      // Also persist on the cached user so other tabs/pages see the new branding
      const u = JSON.parse(localStorage.getItem("rxp_user") || "{}");
      u.branding = data;
      localStorage.setItem("rxp_user", JSON.stringify(u));
      toast.success("Branding saved");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="p-6 max-w-3xl" data-testid="branding-page">
      <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Work Sans" }}>Hospital Branding</h1>
      <p className="text-sm text-slate-500 mt-1">Customize the logo and color scheme that staff see throughout the app.</p>

      <Card className="mt-5 p-5 space-y-5">
        <div className="flex items-start gap-5">
          <div className="w-44 h-44 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-center overflow-hidden">
            {form.logo_url ? (
              <img src={form.logo_url} alt="Logo" className="max-h-full max-w-full object-contain" />
            ) : (
              <Palette size={42} className="text-slate-300" />
            )}
          </div>
          <div className="flex-1 space-y-2">
            <div className="text-sm font-semibold text-slate-700">Hospital logo</div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="branding-logo-file" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={!isAdmin}>
              <Upload size={14} className="mr-1" />Upload logo
            </Button>
            {!isAdmin && <div className="text-xs text-slate-500">Only admin / hospital_admin can change branding.</div>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Primary color</div>
            <div className="flex items-center gap-2">
              <input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="h-11 w-14 rounded-lg border border-slate-300 cursor-pointer" disabled={!isAdmin} data-testid="branding-primary" />
              <Input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="h-11 max-w-[160px] font-mono" disabled={!isAdmin} />
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Accent color</div>
            <div className="flex items-center gap-2">
              <input type="color" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="h-11 w-14 rounded-lg border border-slate-300 cursor-pointer" disabled={!isAdmin} data-testid="branding-accent" />
              <Input value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="h-11 max-w-[160px] font-mono" disabled={!isAdmin} />
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="rounded-xl border border-slate-200 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">Live preview</div>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-white border border-slate-200 flex items-center justify-center overflow-hidden">
              {form.logo_url ? <img src={form.logo_url} alt="" className="max-h-full max-w-full object-contain" /> : <Palette size={20} className="text-slate-300" />}
            </div>
            <button type="button" className="h-10 px-4 rounded-lg text-white font-semibold" style={{ backgroundColor: form.primary_color }}>Primary</button>
            <button type="button" className="h-10 px-4 rounded-lg text-white font-semibold" style={{ backgroundColor: form.accent_color }}>Accent</button>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={busy || !isAdmin} className="bg-blue-700 hover:bg-blue-800" data-testid="branding-save"><Save size={14} className="mr-1" />Save branding</Button>
        </div>
      </Card>
    </div>
  );
}
